import {BookOpen, CheckCircle} from 'lucide-react'

const GUIDE_STEPS = [
  {
    title: "Record the Breeding Date",
    content:
      "Note the exact date your cow was bred, whether through natural service or artificial insemination. Accurate records are the foundation of pregnancy tracking and calving date prediction.",
  },
  {
    title: "Know Your Breed's Gestation Length",
    content:
      "Cattle gestation averages around 283 days, but varies by breed. Holstein and Jersey cows average 279 days, while Highland cattle can carry for 290 days. Beef breeds like Angus (281 days) and Charolais (289 days) also differ.",
  },
  {
    title: "Calculate the Expected Calving Date",
    content:
      "Add the breed-specific gestation days to your breeding date. Our cattle gestation calculator does this automatically, accounting for breed variations and providing trimester tracking.",
  },
  {
    title: "Monitor Throughout Pregnancy",
    content:
      "Track your cow through each trimester. The first trimester focuses on embryo development, the second on fetal growth, and the third on preparing for delivery. Adjust nutrition and veterinary care accordingly.",
  },
]

const KEY_FACTS = [
  "Average cow pregnancy duration is 279-292 days depending on breed",
  "First-calf heifers may calve 3-5 days earlier than mature cows",
  "Bull calves tend to have slightly longer gestation periods",
  "Nutrition and environmental stress can affect calving date by several days",
  "Twin pregnancies typically result in earlier delivery times",
  "Monitoring body condition score helps predict calving readiness",
]

export default function GuideSection() {
  return (
    <section id="guide" className="py-12 sm:py-16 lg:py-20 bg-[#FEFAE0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Expert Resource
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            How to Calculate Cow Gestation
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-2xl mx-auto">
            A practical guide to cattle pregnancy tracking, calving date estimation, and breeding cycle management for farmers and ranchers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Steps */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-[#2D6A4F]" />
              <h3 className="font-bold text-lg text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                Step-by-Step Gestation Guide
              </h3>
            </div>

            <div className="space-y-0">
              {GUIDE_STEPS.map((step, index) => (
                <div key={step.title} className="relative flex gap-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-[#2D6A4F] text-[#FEFAE0] flex items-center justify-center font-bold text-sm shrink-0 z-10">
                      {index + 1}
                    </div>
                    {index < GUIDE_STEPS.length - 1 && (
                      <div className="w-0.5 flex-1 bg-[#B7E4C7] my-1" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pb-8">
                    <h4 className="font-bold text-[#283618] text-base mb-1.5">{step.title}</h4>
                    <p className="text-sm sm:text-base text-[#606C38] leading-relaxed">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Facts Sidebar */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-[#B7E4C7]/40 p-6 shadow-sm sticky top-24">
              <h3 className="font-bold text-lg text-[#283618] mb-4" style={{ fontFamily: "'Merriweather', serif" }}>
                Key Cattle Pregnancy Facts
              </h3>
              <ul className="space-y-3">
                {KEY_FACTS.map((fact) => (
                  <li key={fact} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#40916C] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#606C38] leading-relaxed">{fact}</span>
                  </li>
                ))}
              </ul>

              {/* Breed Table */}
              <div className="mt-6 pt-5 border-t border-[#B7E4C7]/30">
                <h4 className="font-semibold text-sm text-[#283618] mb-3">Gestation by Breed</h4>
                <div className="space-y-2">
                  {[
                    { breed: "Holstein", days: 279 },
                    { breed: "Jersey", days: 279 },
                    { breed: "Angus", days: 281 },
                    { breed: "Beef (Avg)", days: 283 },
                    { breed: "Hereford", days: 285 },
                    { breed: "Simmental", days: 287 },
                    { breed: "Charolais", days: 289 },
                    { breed: "Highland", days: 290 },
                    { breed: "Brahman", days: 292 },
                  ].map((item) => (
                    <div key={item.breed} className="flex items-center justify-between text-sm">
                      <span className="text-[#283618] font-medium">{item.breed}</span>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 rounded-full bg-[#B7E4C7]" style={{ width: `${(item.days - 275) * 4}px` }}>
                          <div className="h-full rounded-full bg-[#2D6A4F]" style={{ width: "100%" }} />
                        </div>
                        <span className="text-[#606C38] font-mono text-xs w-10 text-right">{item.days}d</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
